"use client";

import { IconDownload } from "@tabler/icons-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/lib/auth";
import { useBillingOverview } from "@/lib/billing/useBillingOverview";
import { useStripePortal } from "@/lib/billing/useStripePortal";
import { orgRoleCanManageBilling } from "@/lib/org/org-constants";
import { cn } from "@/lib/utils";
import { formatCurrencyFromMinorUnits, formatDateShort } from "@/lib/utils/formatters";

const tableHeadClass = "h-auto px-4 pb-3 pt-0 text-xs font-medium text-muted-foreground first:pl-0 last:pr-0";
const tableCellClass = "px-4 py-3 first:pl-0 last:pr-0";

const formatInvoiceStatus = (status?: string | null) => {
  if (!status) {
    return "Unknown";
  }

  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

export const BillingInvoiceTab = () => {
  const { orgRole } = useAuth();
  const canManageBilling = orgRoleCanManageBilling(orgRole);
  const { data } = useBillingOverview();
  const { openPortal } = useStripePortal();

  const invoices = data?.billing_overview.invoices ?? [];

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-medium text-foreground">Invoice history</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Invoices are issued when credits are purchased. All dates are in UTC.
          </p>
        </div>
        {canManageBilling ? (
          <Button variant="outline" size="sm" onClick={() => void openPortal()}>
            View in billing portal
          </Button>
        ) : null}
      </div>

      <Table>
        <TableHeader>
          <TableRow className="border-b border-border hover:bg-transparent">
            <TableHead className={tableHeadClass}>Date</TableHead>
            <TableHead className={cn(tableHeadClass, "text-right")}>Amount</TableHead>
            <TableHead className={tableHeadClass}>Status</TableHead>
            <TableHead className={tableHeadClass}>Invoice</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.length ? (
            invoices.map((invoice) => {
              const normalized = (invoice.status ?? "").toLowerCase();
              const isPaid = normalized === "paid";

              return (
                <TableRow key={invoice.id} data-invoice-id={invoice.id} className="border-b border-border">
                  <TableCell className={cn(tableCellClass, "text-sm text-foreground")}>
                    {formatDateShort(invoice.created ?? null)}
                  </TableCell>
                  <TableCell className={cn(tableCellClass, "text-right text-sm font-medium tabular-nums")}>
                    {formatCurrencyFromMinorUnits(invoice.total)}
                  </TableCell>
                  <TableCell className={tableCellClass}>
                    <Badge
                      variant="secondary"
                      className={cn(
                        "rounded-full capitalize",
                        isPaid &&
                          "border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
                      )}
                    >
                      {formatInvoiceStatus(invoice.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className={cn(tableCellClass, "max-w-56 sm:max-w-xs")}>
                    <div className="flex min-w-0 items-center justify-between gap-2">
                      <span className="truncate font-mono text-xs text-muted-foreground tabular-nums">
                        {invoice.number ?? invoice.id}
                      </span>
                      {invoice.invoice_pdf || invoice.hosted_invoice_url ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-7 shrink-0 text-muted-foreground hover:text-foreground"
                              asChild
                            >
                              <a
                                href={invoice.invoice_pdf ?? invoice.hosted_invoice_url ?? "#"}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Download PDF"
                              >
                                <IconDownload className="size-4" aria-hidden />
                              </a>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent sideOffset={6}>Download PDF</TooltipContent>
                        </Tooltip>
                      ) : (
                        <span className="shrink-0 text-xs text-muted-foreground">Unavailable</span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={4} className={cn(tableCellClass, "py-4 text-sm text-muted-foreground")}>
                {canManageBilling ? "No invoices available yet." : "Invoice history is visible to billing admins."}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </section>
  );
};
